import React, { useEffect, useState } from "react";
import { api, BACKEND_URL } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { toast } from "sonner";
import { Plus, Trash2, Link as LinkIcon, Eye, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const FIELD_TYPES = ["text", "email", "phone", "textarea", "select", "photo", "file"];

export default function Forms() {
  const { t } = useI18n();
  const [forms, setForms] = useState([]);
  const [open, setOpen] = useState(false);
  const [subsOpen, setSubsOpen] = useState(false);
  const [currentForm, setCurrentForm] = useState(null);
  const [editing, setEditing] = useState(null);
  const empty = { title: "", description: "", is_active: true, fields: [{ label: "Name", type: "text", required: true, options: [] }] };
  const [form, setForm] = useState(empty);

  const load = async () => { const { data } = await api.get("/forms"); setForms(data); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (f) => { setEditing(f); setForm({ ...f, fields: (f.fields || []).map((fi) => ({ ...fi, options: fi.options || [] })) }); setOpen(true); };

  const addField = () => setForm((s) => ({ ...s, fields: [...s.fields, { label: "", type: "text", required: false, options: [] }] }));
  const removeField = (i) => setForm((s) => ({ ...s, fields: s.fields.filter((_, idx) => idx !== i) }));
  const updField = (i, key, v) => setForm((s) => ({ ...s, fields: s.fields.map((fi, idx) => idx === i ? { ...fi, [key]: v } : fi) }));

  const save = async () => {
    if (!form.title) return toast.error("Title required");
    const payload = {
      ...form,
      fields: form.fields.map((fi) => ({ ...fi, options: typeof fi.options === "string" ? fi.options.split(",").map((s) => s.trim()).filter(Boolean) : (fi.options || []) })),
    };
    if (editing) await api.put(`/forms/${editing.id}`, payload);
    else await api.post("/forms", payload);
    toast.success("OK"); setOpen(false); load();
  };

  const del = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/forms/${id}`);
    load();
  };

  const publicLink = (slug) => `${window.location.origin}/f/${slug}`;

  const copyLink = (slug) => {
    navigator.clipboard.writeText(publicLink(slug));
    toast.success(t("copied"));
  };

  const viewSubs = async (f) => {
    const { data } = await api.get(`/forms/${f.id}`);
    setCurrentForm(data);
    setSubsOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="forms-root">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Public</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">{t("forms")}</h1>
        </div>
        <button data-testid="open-new-form" onClick={openNew} className="btn-primary inline-flex items-center gap-2"><Plus size={16}/>{t("add_form")}</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {forms.length === 0 && <div className="text-zinc-500 text-sm">{t("no_data")}</div>}
        {forms.map((f) => (
          <div key={f.id} className="swiss-card p-5 fade-up" data-testid={`form-${f.id}`}>
            <div className="flex items-start justify-between mb-2">
              <div className="font-display font-bold text-lg">{f.title}</div>
              <button onClick={() => del(f.id)} className="text-zinc-400 hover:text-[#E63946]"><Trash2 size={14}/></button>
            </div>
            <p className="text-sm text-zinc-600 mb-3 line-clamp-2">{f.description || "—"}</p>
            <div className="text-xs text-zinc-500 mb-3">{f.fields?.length || 0} fields</div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => copyLink(f.share_slug)} data-testid={`copy-link-${f.id}`} className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 border border-zinc-300 hover:bg-zinc-50" style={{borderRadius: 2}}>
                <LinkIcon size={12}/> {t("copy_link")}
              </button>
              <button onClick={() => openEdit(f)} className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 border border-zinc-300 hover:bg-zinc-50" style={{borderRadius: 2}}>
                {t("edit")}
              </button>
              <button onClick={() => viewSubs(f)} data-testid={`view-subs-${f.id}`} className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 klein-blue" style={{borderRadius: 2}}>
                <Eye size={12}/> {t("view_submissions")}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? t("edit") : t("add_form")}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto p-1">
            <input data-testid="form-title" placeholder={t("form_title")} className="w-full border border-zinc-300 px-3 py-2" style={{borderRadius: 2}} value={form.title} onChange={(e) => setForm({...form, title: e.target.value})}/>
            <textarea placeholder={t("form_description")} rows={2} className="w-full border border-zinc-300 px-3 py-2" style={{borderRadius: 2}} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}/>
            <div className="flex items-center gap-2">
              <input id="active" type="checkbox" checked={form.is_active} onChange={(e) => setForm({...form, is_active: e.target.checked})}/>
              <label htmlFor="active" className="text-sm">Active</label>
            </div>
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-[0.15em] text-zinc-500">{t("fields")}</div>
              <button onClick={addField} data-testid="add-field" className="text-xs inline-flex items-center gap-1 px-2 py-1 border border-zinc-300"><Plus size={12}/> {t("add_field")}</button>
            </div>
            {form.fields.map((fi, i) => (
              <div key={i} className="border border-zinc-200 p-3 space-y-2" style={{borderRadius: 2}}>
                <div className="flex gap-2">
                  <input data-testid={`field-label-${i}`} placeholder={t("field_label")} className="flex-1 border border-zinc-300 px-2 py-1.5 text-sm" style={{borderRadius: 2}} value={fi.label} onChange={(e) => updField(i, "label", e.target.value)}/>
                  <select className="border border-zinc-300 px-2 py-1.5 text-sm bg-white" style={{borderRadius: 2}} value={fi.type} onChange={(e) => updField(i, "type", e.target.value)}>
                    {FIELD_TYPES.map((ft) => <option key={ft} value={ft}>{ft}</option>)}
                  </select>
                  <label className="inline-flex items-center gap-1 text-xs text-zinc-600 px-2">
                    <input type="checkbox" checked={fi.required} onChange={(e) => updField(i, "required", e.target.checked)}/> {t("required")}
                  </label>
                  <button onClick={() => removeField(i)} className="text-zinc-400 hover:text-[#E63946]"><X size={14}/></button>
                </div>
                {fi.type === "select" && (
                  <input placeholder={t("options_csv")} className="w-full border border-zinc-300 px-2 py-1.5 text-sm" style={{borderRadius: 2}}
                    value={Array.isArray(fi.options) ? fi.options.join(", ") : fi.options}
                    onChange={(e) => updField(i, "options", e.target.value)}/>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 border border-zinc-300">{t("cancel")}</button>
            <button data-testid="form-save" onClick={save} className="btn-primary">{t("save")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submissions dialog */}
      <Dialog open={subsOpen} onOpenChange={setSubsOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{currentForm?.title} — {t("submissions")}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-2">
            {currentForm?.submissions?.length === 0 && <div className="text-zinc-500 text-sm">{t("no_data")}</div>}
            {currentForm?.submissions?.map((s) => (
              <div key={s.id} className="border border-zinc-200 p-3" style={{borderRadius: 2}}>
                <div className="text-xs text-zinc-500 mb-1">{new Date(s.created_at).toLocaleString()}</div>
                <div className="text-sm grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1">
                  {Object.entries(s.data || {}).map(([k, v]) => {
                    if (typeof v === "string" && v.startsWith("data:image")) {
                      return <div key={k}><span className="text-zinc-500">{k}:</span> <img src={v} alt="" className="inline-block w-16 h-16 object-cover ml-1 border border-zinc-200" style={{borderRadius: 2}}/></div>;
                    }
                    if (typeof v === "string" && v.startsWith("data:")) {
                      return <div key={k}><span className="text-zinc-500">{k}:</span> <span className="text-[#002FA7]">[file]</span></div>;
                    }
                    return <div key={k} className="truncate"><span className="text-zinc-500">{k}:</span> {String(v)}</div>;
                  })}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
